import type { PasswordResetFailedAction } from '@Application/Security/PasswordResets/Actions/passwordResetFailed';
import type { PasswordResetFetchFailedAction } from '@Application/Security/PasswordResets/Actions/passwordResetFetchFailed';
import PerformPasswordReset from '@Application/Security/PasswordResets/Commands/PerformPasswordReset';
import fetchPasswordReset from '@Application/Security/PasswordResets/fetchPasswordReset';
import performPasswordReset from '@Application/Security/PasswordResets/performPasswordReset';
import FetchPasswordReset from '@Application/Security/PasswordResets/Queries/FetchPasswordReset';
import NewPasswordFormData from '@UI/Pages/Authentication/Shared/FormData/NewPasswordFormData';
import PasswordResetNotifications from '@UI/Pages/Authentication/PasswordReset/PasswordResetNotifications';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Button, Card, Form, Message, Password, Spinner } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

type ResetState =
	| { readonly type: 'loading' }
	| { readonly type: 'ready'; readonly email: string }
	| { readonly type: 'expired'; readonly message: string }
	| { readonly type: 'invalid' };

/**
 * A reset always answers `204` and revokes every refresh token of the account: no session opens
 * here, the visitor is sent back to sign in explicitly.
 */
const PasswordResetPage: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [state, setState] = React.useState<ResetState>({ type: 'loading' });
	const [formData, setFormData] = React.useState(() => NewPasswordFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (token === undefined) {
			setState({ type: 'invalid' });
			return undefined;
		}

		let isMounted = true;

		dispatch(fetchPasswordReset(new FetchPasswordReset(token)))
			.then((action) => {
				if (!isMounted) {
					return;
				}

				// The dispatcher only ever settles this promise on the matching action, but the
				// dispatch signature stays generic: narrow explicitly instead of asserting the type.
				if (action.type !== 'PASSWORD_RESET_FETCHED') {
					setState({ type: 'invalid' });
					return;
				}

				setState({ type: 'ready', email: action.payload.email });
			})
			.catch((action: PasswordResetFetchFailedAction) => {
				if (!isMounted) {
					return;
				}

				setState(
					action.reason === 'gone'
						? { type: 'expired', message: action.message }
						: { type: 'invalid' },
				);
			});

		return () => {
			isMounted = false;
		};
	}, [dispatch, token]);

	const handlePasswordChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPassword(value));
	}, []);

	const handlePasswordConfirmationChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPasswordConfirmation(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (token === undefined) {
			return;
		}

		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsSubmitting(true);

		dispatch(performPasswordReset(new PerformPasswordReset(token, formData.toCommandData().password)))
			.then(() => {
				navigate('/login', { replace: true });
			})
			.catch((action: PasswordResetFailedAction) => {
				if (action.reason === 'invalid') {
					updateValidationFromExternalSource({
						action: {
							type: action.type,
							payload: { fieldErrors: action.payload?.fieldErrors ?? [] },
						},
						eventNames: 'PASSWORD_RESET_FAILED',
						formDataUpdater: setFormData,
					});
					return;
				}

				if (action.reason === 'unknown') {
					// A transient failure (server error, network cut) is no proof the token is dead: leave
					// the form and the password the visitor typed in place rather than discard both.
					return;
				}

				// The token died between the `GET` and this `POST` (expired, or consumed by a concurrent
				// submission), or it no longer resolves at all: drop the form either way.
				setState(
					action.reason === 'gone'
						? { type: 'expired', message: action.message }
						: { type: 'invalid' },
				);
			})
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData, navigate, token]);

	const renderContent = (): React.ReactElement => {
		switch (state.type) {
			case 'loading': {
				return <Spinner aria-label="Chargement du lien de réinitialisation" />;
			}
			case 'expired': {
				return (
					<React.Fragment>
						<Message severity="warn" text={state.message} />
						<p>
							<Link to="/password/forgotten">Demander un nouveau lien</Link>
						</p>
					</React.Fragment>
				);
			}
			case 'invalid': {
				return <Message severity="error" text="Ce lien de réinitialisation est invalide." />;
			}
			case 'ready': {
				return (
					<React.Fragment>
						<p css={fieldMarginStyles}>
							Choisissez un nouveau mot de passe pour <strong>{state.email}</strong>.
						</p>
						<Form onSubmit={handleSubmit} aria-label="Réinitialisation du mot de passe">
							<Password
								name="password"
								label="Mot de passe"
								subLabel="12 caractères minimum."
								feedback={false}
								toggleMask
								autoComplete="new-password"
								required
								value={formData.password}
								onChange={handlePasswordChange}
								errorMessage={formData.getValidationMessage('password')}
								isFormFieldValid={formData.isValid('password')}
							/>
							<Password
								name="passwordConfirmation"
								label="Confirmation du mot de passe"
								feedback={false}
								toggleMask
								autoComplete="new-password"
								required
								value={formData.passwordConfirmation}
								onChange={handlePasswordConfirmationChange}
								errorMessage={formData.getValidationMessage('passwordConfirmation')}
								isFormFieldValid={formData.isValid('passwordConfirmation')}
							/>
							<Button
								type="submit"
								label="Réinitialiser le mot de passe"
								loading={isSubmitting}
								css={fieldMarginStyles}
							/>
						</Form>
					</React.Fragment>
				);
			}
		}
	};

	return (
		<React.Fragment>
			<Card title="Nouveau mot de passe" width="100%" maxWidth="26rem">
				{renderContent()}
			</Card>
			<PasswordResetNotifications />
		</React.Fragment>
	);
};

export default PasswordResetPage;
